/**
 * BBYM Lead Forms (shared)
 * - Turnstile-gated submit button (disabled until solved)
 * - AJAX submit to Cloudflare Worker (no page reload)
 * - Flash overlay success/error
 * - Safe for Shopify section reloads
 *
 * Expected markup:
 * - Wrapper: .bbym-lead-form-wrapper (we also support .bbym-corp-form-wrapper)
 * - Form: .corporate-form--form
 * - Flash: .flash-message inside wrapper
 * - Turnstile widget: .cf-turnstile (auto-render) with sitekey + callbacks set in markup
 * - Submit input/button exists and starts disabled in Liquid
 */
(function () {
  // --- Turnstile callback state ---
  window.BBYM_Turnstile = window.BBYM_Turnstile || { solved: false };

  function setAllSubmitButtonsDisabled(disabled) {
    document.querySelectorAll('form.corporate-form--form').forEach((form) => {
      const btn = form.querySelector('input[type="submit"], button[type="submit"]');
      if (btn) btn.disabled = disabled;
    });
  }

  // Define callbacks only once (prevents redeclare issues if multiple sections include this asset)
  if (!window.BBYM_onTurnstileSuccess) {
    window.BBYM_onTurnstileSuccess = function (token) {
      window.BBYM_Turnstile.solved = !!token;
      setAllSubmitButtonsDisabled(!window.BBYM_Turnstile.solved);
    };
    window.BBYM_onTurnstileExpired = function () {
      window.BBYM_Turnstile.solved = false;
      setAllSubmitButtonsDisabled(true);
    };
    window.BBYM_onTurnstileError = function () {
      window.BBYM_Turnstile.solved = false;
      setAllSubmitButtonsDisabled(true);
    };
  }

  // Load Turnstile script only once
  function ensureTurnstileScriptOnce() {
    if (document.querySelector('script[src*="challenges.cloudflare.com/turnstile/v0/api.js"]')) return;
    const s = document.createElement('script');
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    s.async = true;
    s.defer = true;
    document.head.appendChild(s);
  }

  // --- Flash helpers (per wrapper) ---
  function getFlashElements(wrapper) {
    const flash = wrapper.querySelector('.flash-message');
    return {
      flash,
      content: flash?.querySelector('.flash-message--content'),
      closeBtn: flash?.querySelector('button.close'),
      overlay: flash?.querySelector('.flash-message--overlay')
    };
  }

  function openFlash(wrapper, html) {
    const { flash, content } = getFlashElements(wrapper);
    if (!flash || !content) return;

    content.innerHTML = html;

    // Support both historical CSS class names
    flash.classList.add('show', 'is-visible');
    flash.setAttribute('aria-hidden', 'false');

    function onEscape(e) {
      if (e.key === 'Escape') closeFlash(wrapper);
    }
    // Store handler so we can remove it
    wrapper._bbymFlashEscapeHandler = onEscape;
    window.addEventListener('keydown', onEscape);
  }

  function closeFlash(wrapper) {
    const { flash } = getFlashElements(wrapper);
    if (!flash) return;

    flash.classList.remove('show', 'is-visible');
    flash.setAttribute('aria-hidden', 'true');

    if (wrapper._bbymFlashEscapeHandler) {
      window.removeEventListener('keydown', wrapper._bbymFlashEscapeHandler);
      wrapper._bbymFlashEscapeHandler = null;
    }
  }

  // --- Form init ---
  function initWrapper(wrapper) {
    const form = wrapper.querySelector('form.corporate-form--form');
    if (!form) return;

    // Avoid double-binding on theme editor reloads
    if (form.dataset.bbymBound === 'true') return;
    form.dataset.bbymBound = 'true';

    ensureTurnstileScriptOnce();

    const submitBtn = form.querySelector('input[type="submit"], button[type="submit"]');
    const { closeBtn, overlay, content } = getFlashElements(wrapper);

    // Default success HTML = whatever is in the flash markup
    const DEFAULT_SUCCESS_HTML = content?.innerHTML || (
      "Thanks for reaching out to Baked by Melissa! We'll be in touch within one business day."
    );

    closeBtn?.addEventListener('click', () => closeFlash(wrapper));
    overlay?.addEventListener('click', () => closeFlash(wrapper));

    // Start disabled (Turnstile must solve)
    if (submitBtn) submitBtn.disabled = true;

    form.addEventListener('submit', async (e) => {
      e.preventDefault();

      // Native constraint validation UI
      if (!form.checkValidity()) {
        form.reportValidity();
        return;
      }

      // Honeypot
      const hp = form.querySelector('input[name="website"]');
      if (hp && hp.value) return;

      // Hard gate: must be solved
      if (!window.BBYM_Turnstile?.solved) {
        openFlash(wrapper, 'Please complete the verification and try again.');
        if (submitBtn) submitBtn.disabled = true;
        return;
      }

      // Token must exist
      const ts = form.querySelector('input[name="cf-turnstile-response"]');
      if (!ts || !ts.value) {
        openFlash(wrapper, 'Please complete the verification and try again.');
        if (submitBtn) submitBtn.disabled = true;
        return;
      }

      const originalLabel = submitBtn?.value;
      if (submitBtn) {
        submitBtn.disabled = true;
        if ('value' in submitBtn) submitBtn.value = 'Submitting…';
      }

      try {
        const res = await fetch(form.action, {
          method: 'POST',
          body: new FormData(form),
          mode: 'cors',
          credentials: 'omit',
          headers: { 'Accept': 'application/json' },
          redirect: 'follow'
        });

        // Prefer worker JSON 200 OK. If some env returns redirect, treat 2xx/3xx as success.
        if (!(res.status >= 200 && res.status < 400)) {
          const text = await res.text().catch(() => '');
          throw new Error(`Request failed (${res.status}). ${text}`);
        }

        let messageHtml = DEFAULT_SUCCESS_HTML;
        const ct = res.headers.get('content-type') || '';
        if (ct.includes('application/json')) {
          const payload = await res.json().catch(() => null);
          if (payload?.message) messageHtml = payload.message;
        }

        openFlash(wrapper, messageHtml);

        // Reset form
        form.reset();

        // Require re-solve
        window.BBYM_Turnstile.solved = false;

        if (window.turnstile) {
          try { window.turnstile.reset(); } catch (_) {}
        }

        if (submitBtn) submitBtn.disabled = true;
      } catch (err) {
        console.error(err);
        openFlash(wrapper, 'Sorry — something went wrong submitting the form. Please try again.');

        window.BBYM_Turnstile.solved = false;

        if (window.turnstile) {
          try { window.turnstile.reset(); } catch (_) {}
        }

        if (submitBtn) submitBtn.disabled = true;
      } finally {
        if (submitBtn && originalLabel && 'value' in submitBtn) submitBtn.value = originalLabel;
      }
    });
  }

  function initAll() {
    // Support both wrappers (old + new)
    const wrappers = [
      ...document.querySelectorAll('.bbym-lead-form-wrapper'),
      ...document.querySelectorAll('.bbym-corp-form-wrapper')
    ];

    // De-dupe if both classes exist on same element
    const unique = Array.from(new Set(wrappers));
    unique.forEach(initWrapper);
  }

  document.addEventListener('DOMContentLoaded', initAll);
  document.addEventListener('shopify:section:load', initAll);
})();